import { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TourDialog } from '@/components/common/WhatsNew';
import { toast } from 'sonner';
import { useGuidedTour } from '@/components/tour/GuidedTour';
import {
  GraduationCap, CalendarDays, Layers, Database, Users, BookOpen, Search,
  Sparkles, ArrowRight, Home, Compass,
} from 'lucide-react';

const SECTIONS = [
  {
    icon: Home, title: 'Start your day on Home',
    intro: 'Your dashboard is a quiet snapshot of what matters today.',
    steps: [
      'The tiles at the top show your projects, open tasks, and unread alerts.',
      '"At a glance" shows your task progress and projects by stage — visually.',
      'Cards below take you straight to the areas you use most.',
    ],
    link: { to: '/home', label: 'Open Home' },
  },
  {
    icon: CalendarDays, title: 'Plan your week',
    intro: 'My Week is your personal planner for the next 7 days.',
    steps: [
      'Click "New commitment" to add a task with a target date.',
      'Drag a card between Today / This week / Later to move its date.',
      'Hit "Done" when finished — or "Shift" with a quick reason if priorities changed.',
      'Milestones you own appear in a strip at the top with the same Done / Shift buttons.',
    ],
    link: { to: '/my-week', label: 'Open My Week' },
  },
  {
    icon: Layers, title: 'Track a project',
    intro: 'Every project sits on a stage board from Strategy & Concept to Closure.',
    steps: [
      'The Projects board shows each stage as a column — click any card for full detail.',
      'Switch to List view for a sortable table.',
      'The coloured strip at the top shows how projects spread across stages.',
    ],
    link: { to: '/projects', label: 'Open Projects' },
  },
  {
    icon: Database, title: 'Master Project Data',
    intro: 'One canonical list of all projects — the single source of truth.',
    steps: [
      'Everyone sees the projects relevant to them; money is visible only to finance roles.',
      'Owner & Design Leads: click "Download Template" to get the Excel format.',
      'Fill it in, then "Import Data" — every row is validated and previewed before anything is saved.',
      'The master Google Sheet is saved as a clickable link — paste the URL once, open it anytime.',
    ],
    link: { to: '/master-projects', label: 'Open Master Data' },
  },
  {
    icon: Users, title: 'Find people & who does what',
    intro: 'The Team page has two tabs: Directory and Resource Mapping.',
    steps: [
      'Directory: every colleague with role, department, and a one-click email link.',
      'Resource Mapping: each project with its leads and team members, plus a load chart per person.',
    ],
    link: { to: '/team', label: 'Open Team' },
  },
  {
    icon: BookOpen, title: 'Knowledge Hub & Drive folder',
    intro: 'SOPs, templates and reference pages — all in one place.',
    steps: [
      'Browse SOPs A–H in the sidebar, or pin the pages you use most.',
      'The Google Drive card at the top opens the shared folder in a new tab.',
      'Owner & Ops can set or change the Drive link — everyone else just clicks it.',
    ],
    link: { to: '/knowledge', label: 'Open Knowledge Hub' },
  },
  {
    icon: Search, title: 'Move fast with Ctrl/⌘ K',
    intro: 'The search bar at the top jumps you anywhere in two keystrokes.',
    steps: [
      'Press Ctrl+K (or ⌘K on Mac) from any screen.',
      'Type to search pages and projects, or pick a quick action like "New commitment".',
      'Your 5 most recent pages are always at the top for instant return.',
    ],
  },
];

export default function GuidePage() {
  const [tour, setTour] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const { restart: restartGuidedTour } = useGuidedTour();

  const replayTour = async () => {
    try {
      const r = await api.get('/releases/whats-new');
      if (r.data?.available) { setTour(r.data); setTourOpen(true); }
      else toast.info('The What’s New tour unlocks on release day.');
    } catch { toast.error('Could not load the tour.'); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'How to use' }]}>
      <div className="p-6 max-w-[1100px] mx-auto space-y-5 fade-up" data-testid="guide-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <GraduationCap size={22} />How to use 32DECEMBER OS
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[640px]">
              A two-minute guide to the key flows. Come back anytime — this page never moves.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={restartGuidedTour} data-testid="guide-restart-tour">
              <Compass size={13} className="mr-1" />Restart guided tour
            </Button>
            <Button variant="outline" size="sm" onClick={replayTour} data-testid="guide-replay-tour">
              <Sparkles size={13} className="mr-1" />Replay the What&rsquo;s New tour
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SECTIONS.map((s, i) => (
            <div key={s.title} className="card p-5 elev-1 flex flex-col" data-testid={`guide-section-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-none"
                     style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>
                  <s.icon size={17} />
                </div>
                <div className="font-heading font-semibold text-[15.5px]">{s.title}</div>
              </div>
              <p className="text-[12.5px] text-[hsl(var(--foreground-muted))] mb-2">{s.intro}</p>
              <ol className="space-y-1.5 flex-1">
                {s.steps.map((st, j) => (
                  <li key={j} className="flex items-start gap-2 text-[13px] leading-relaxed">
                    <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10.5px] font-semibold flex-none mt-0.5"
                          style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}>
                      {j + 1}
                    </span>
                    <span>{st}</span>
                  </li>
                ))}
              </ol>
              {s.link && (
                <Link to={s.link.to}
                      className="text-[12.5px] text-[hsl(var(--primary))] underline flex items-center gap-1 mt-3 w-fit"
                      data-testid={`guide-link-${i}`}>
                  {s.link.label} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
      <TourDialog release={tour} open={tourOpen} onClose={() => setTourOpen(false)} />
    </AppShell>
  );
}
